import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Music, Calendar, FileText, LogOut, Settings, Sparkles, Inbox, PieChart, FolderOpen, Users, Shield, Zap, LayoutDashboard, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { HighlightsManager } from "@/components/admin/HighlightsManager";
import { ExecutiveDashboard } from "@/components/admin/ExecutiveDashboard";

import { InquiriesTable } from "@/components/admin/InquiriesTable";
import TeamManager from "@/components/admin/TeamManager";
import SiteSettingsManager from "@/components/admin/SiteSettingsManager";
import { useIsAdmin } from "@/hooks/useUserRole";
import { SetPasswordDialog } from "@/components/account/SetPasswordDialog";
import { FilesManager } from "@/components/admin/FilesManager";
import { AdminUserManager } from "@/components/admin/AdminUserManager";
import { Logo } from "@/components/Logo";



interface Profile {
  name: string | null;
  email: string;
}

interface Submission {
  id: string;
  event_date: string;
  venue_address: string;
  created_at: string;
  event_dates_json?: any;
  packages_json?: any;
  vibes?: string[] | null;
  total_cost?: number | null;
  occasion?: string | null;
  status?: string;
}


const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);


  useEffect(() => {
    checkAuth();

    // Subscribe to realtime updates for status changes
    const channel = supabase
      .channel('user-submissions')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contact_submissions',
        },
        (payload) => {
          // Update the submission in state when status changes
          setSubmissions((prev) =>
            prev.map((sub) =>
              sub.id === payload.new.id
                ? { ...sub, status: payload.new.status }
                : sub
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    // Load profile
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("name, email")
      .eq("id", session.user.id)
      .single();

    if (profileError) {
      console.error("Failed to load profile:", profileError);
    }

    if (profileData) {
      setProfile(profileData);
    }

    // Check if user is the superadmin (earliest admin by created_at)
    const { data: adminUsers } = await supabase
      .from('user_roles')
      .select('user_id, created_at')
      .eq('role', 'admin')
      .order('created_at', { ascending: true });
    
    if (adminUsers && adminUsers.length > 0) {
      const superAdminId = adminUsers[0].user_id;
      setIsSuperAdmin(session.user.id === superAdminId);
    }

    // Load submissions - query by user_id OR matching email for unlinked submissions
    // RLS policies now handle access control - just fetch all accessible records
    const { data: submissionsData, error: submissionsError } = await supabase
      .from("contact_submissions")
      .select("id, event_date, venue_address, created_at, event_dates_json, packages_json, vibes, total_cost, occasion, status")
      .order("created_at", { ascending: false });

    if (submissionsError) {
      console.error("Failed to load inquiries:", submissionsError);
      toast({
        title: "Couldn't load inquiries",
        description: "Please refresh and try again.",
        variant: "destructive",
      });
    }

    if (submissionsData) {
      setSubmissions(submissionsData);
    }

    setIsLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Signed out",
      description: "You've been successfully signed out.",
    });
    navigate("/");
  };

  const handleNewInquiry = async () => {
    // Check if user has a complete profile
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user?.id) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      // If profile exists with required info, skip to event details
      if (profileData && profileData.name && profileData.email && profileData.phone) {
        // Clear previous inquiry's songs from database
        const sessionId = sessionStorage.getItem('session_id') || crypto.randomUUID();
        await supabase
          .from('favorite_songs')
          .delete()
          .eq('session_id', sessionId);
        
        // Generate new session ID for fresh start
        const newSessionId = crypto.randomUUID();
        sessionStorage.setItem('session_id', newSessionId);
        
        navigate("/event-info");
        return;
      }
    }
    
    // Clear and create new session for non-authenticated users too
    const newSessionId = crypto.randomUUID();
    sessionStorage.setItem('session_id', newSessionId);
    
    // Otherwise start from contact form
    navigate("/");
  };

  if (isLoading || isAdminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div 
            className="cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => navigate("/")}
          >
            <Logo size="md" />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {profile?.name || profile?.email}
            </span>
            {isSuperAdmin && (
              <Badge className="bg-gradient-to-r from-warning to-destructive text-white border-0 shadow-lg">
                <Crown className="h-3 w-3 mr-1" />
                Superadmin
              </Badge>
            )}
            {isAdmin && !isSuperAdmin && (
              <Badge variant="outline" className="border-primary/50 text-primary bg-primary/10">
                <Shield className="h-3 w-3 mr-1" />
                Admin
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Welcome back!</h2>
          <p className="text-muted-foreground">
            Explore exclusive content, book events, and enjoy member benefits.
          </p>
        </div>

        <Tabs defaultValue="music" className="space-y-6">
          <TabsList>
            <TabsTrigger value="music">
              <Music className="h-4 w-4 mr-2" />
              Stream Music
            </TabsTrigger>
            <TabsTrigger value="inquiries">
              <FileText className="h-4 w-4 mr-2" />
              My Inquiries
            </TabsTrigger>
            <TabsTrigger value="concerts">
              <Calendar className="h-4 w-4 mr-2" />
              Concerts & Events
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger 
                value="admin"
                className="relative data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-accent/20 data-[state=active]:border-primary/50"
              >
                <Zap className="h-4 w-4 mr-2 text-primary" />
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent font-semibold">
                  Command Center
                </span>
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="inquiries">
            <Card>
              <CardHeader>
                <CardTitle>Your Event Inquiries</CardTitle>
                <CardDescription>
                  View and manage your booking requests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleNewInquiry} className="w-full sm:w-auto">
                  Submit Another Inquiry
                </Button>
                
                {submissions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No inquiries yet. Submit your first event inquiry!
                  </p>
                ) : (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-4">
                      {submissions.map((submission) => (
                        <Card key={submission.id} className="overflow-hidden">
                          <CardHeader className="pb-3 bg-muted/30">
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-lg">
                                  {submission.occasion || 'Event Inquiry'}
                                </CardTitle>
                                <CardDescription>
                                  Submitted {new Date(submission.created_at).toLocaleDateString()}
                                </CardDescription>
                              </div>
                              <div className="text-right">
                                <Badge 
                                  variant="secondary"
                                  className={
                                    submission.status === 'confirmed' ? 'bg-green-500/20 text-green-700 border-green-500/30' :
                                    submission.status === 'call_scheduled' ? 'bg-purple-500/20 text-purple-700 border-purple-500/30' :
                                    submission.status === 'contacted' ? 'bg-blue-500/20 text-blue-700 border-blue-500/30' :
                                    submission.status === 'quoted' ? 'bg-orange-500/20 text-orange-700 border-orange-500/30' :
                                    submission.status === 'completed' ? 'bg-emerald-500/20 text-emerald-700 border-emerald-500/30' :
                                    submission.status === 'cancelled' ? 'bg-red-500/20 text-red-700 border-red-500/30' :
                                    ''
                                  }
                                >
                                  {submission.status === 'call_scheduled' ? 'Call Scheduled' :
                                   submission.status === 'confirmed' ? 'Confirmed' :
                                   submission.status === 'contacted' ? 'Contacted' :
                                   submission.status === 'quoted' ? 'Quoted' :
                                   submission.status === 'completed' ? 'Completed' :
                                   submission.status === 'cancelled' ? 'Cancelled' :
                                   'Pending'}
                                </Badge>
                                {submission.total_cost && (
                                  <p className="text-sm font-semibold text-primary mt-1">
                                    ${submission.total_cost.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-4 space-y-4">
                            {/* Events with details */}
                            {submission.event_dates_json && Array.isArray(submission.event_dates_json) && submission.event_dates_json.length > 0 ? (
                              <div className="space-y-3">
                                {submission.event_dates_json.map((evt: any, idx: number) => {
                                  // Find the package for this event
                                  const eventPackage = submission.packages_json?.find(
                                    (pkg: any) => pkg.eventIndex === idx
                                  );
                                  
                                  return (
                                    <div key={idx} className="border rounded-lg p-3 bg-background">
                                      <div className="flex justify-between items-start mb-2">
                                        <div>
                                          <p className="font-semibold text-sm">
                                            {evt.occasion || `Event ${idx + 1}`}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {evt.date || 'Date TBD'} • {evt.startTime || 'TBD'} - {evt.endTime || 'TBD'}
                                          </p>
                                        </div>
                                        {evt.guestCount && (
                                          <Badge variant="outline" className="text-xs">
                                            {evt.guestCount} guests
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      {/* Venue */}
                                      {evt.venueAddress && (
                                        <p className="text-xs text-muted-foreground mb-2">
                                          📍 {evt.venueAddress}
                                        </p>
                                      )}
                                      
                                      {/* Vibes */}
                                      {evt.vibes && evt.vibes.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {evt.vibes.map((vibe: string, vIdx: number) => (
                                            <Badge key={vIdx} variant="secondary" className="text-xs">
                                              ✨ {vibe}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* Genres */}
                                      {evt.genres && evt.genres.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {evt.genres.map((genre: string, gIdx: number) => (
                                            <Badge key={gIdx} variant="outline" className="text-xs">
                                              🎵 {genre}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* Package/Service Selected */}
                                      {eventPackage && (
                                        <div className="mt-2 pt-2 border-t">
                                          <p className="text-xs font-medium text-primary">
                                            🎶 {eventPackage.name}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {eventPackage.musicians}
                                          </p>
                                          {eventPackage.price && (
                                            <p className="text-xs font-semibold mt-1">
                                              ${eventPackage.price.toLocaleString()}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm">
                                  Event Date: {new Date(submission.event_date).toLocaleDateString()}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {submission.venue_address || 'Venue TBD'}
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="concerts">
            <Card>
              <CardHeader>
                <CardTitle>Exclusive Concerts & Events</CardTitle>
                <CardDescription>
                  Discover upcoming performances and exclusive member events
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center py-8">
                  Concert listings coming soon! Check back for exclusive member events.
                </p>
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="music">
            <div className="space-y-6">
              {/* Hero Section */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-background to-primary/5 p-8 border">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <h2 className="text-3xl font-bold mb-2">Stream The Lost Strings</h2>
                  <p className="text-muted-foreground max-w-lg">
                    Follow us on your favorite platform and never miss a new release
                  </p>
                </div>
              </div>

              {/* Streaming Platforms */}
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {[
                  { name: 'Spotify', color: '#1DB954', url: 'https://open.spotify.com/artist/2EldLZ5d0hetsPUwgkIzrF', icon: 'M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z' },
                  { name: 'Apple', color: '#FA243C', url: 'https://music.apple.com/us/artist/the-lost-strings/1437857686', icon: 'M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.802.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03c.525 0 1.048-.034 1.57-.1.823-.106 1.597-.35 2.296-.81.84-.553 1.472-1.287 1.88-2.208.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.455-2.105-1.326-.38-1.046.025-2.137 1.02-2.69.39-.218.817-.346 1.26-.418.39-.064.784-.115 1.175-.175.254-.04.504-.094.72-.238.2-.133.286-.333.3-.567.003-.06.002-.12.002-.18V7.593c0-.12-.03-.166-.153-.165-1.09.015-2.18.03-3.27.044-.042 0-.084.01-.16.02v6.563c0 .44-.07.867-.284 1.258-.31.567-.784.922-1.4 1.075-.334.083-.673.138-1.016.15-.94.034-1.768-.48-2.083-1.372-.38-1.08.05-2.187 1.1-2.722.382-.195.793-.314 1.216-.376.44-.065.883-.118 1.32-.19.19-.03.377-.09.546-.18.224-.118.34-.32.36-.57.004-.06.002-.12.002-.18V5.806c0-.176.047-.25.222-.285.34-.066.68-.136 1.02-.203 1.11-.22 2.22-.44 3.33-.66.395-.078.79-.16 1.187-.234.087-.016.14.018.14.12-.002 1.857-.002 3.714-.002 5.57z' },
                  { name: 'Instagram', color: '#E4405F', url: 'https://www.instagram.com/theloststrings/', icon: 'M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z' },
                  { name: 'Amazon', color: '#00A8E1', url: 'https://music.amazon.com/artists/B07HY8529T/the-lost-strings', icon: 'M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.685zm3.186 7.705c-.209.189-.512.201-.745.074-1.052-.872-1.238-1.276-1.814-2.106-1.734 1.767-2.962 2.297-5.209 2.297-2.66 0-4.731-1.641-4.731-4.925 0-2.565 1.391-4.309 3.37-5.164 1.715-.754 4.11-.891 5.942-1.095v-.41c0-.753.06-1.642-.383-2.294-.385-.579-1.124-.82-1.775-.82-1.205 0-2.277.618-2.54 1.897-.054.285-.261.566-.549.58l-3.061-.331c-.259-.056-.548-.266-.472-.66C6.013 1.661 8.891 0 11.512 0c1.347 0 3.109.358 4.172 1.381 1.347 1.26 1.218 2.943 1.218 4.776v4.322c0 1.301.541 1.872 1.05 2.574.179.256.218.561-.01.752-.568.475-1.579 1.36-2.133 1.856l-.005.004-.66.534zM21.779 20.5c-1.791 1.323-4.38 2.021-6.611 2.021-3.129 0-5.946-1.156-8.077-3.081-.168-.15-.018-.358.183-.241 2.3 1.339 5.147 2.143 8.085 2.143 1.983 0 4.162-.41 6.168-1.261.302-.128.557.199.252.419zM22.904 19.239c-.227-.292-1.503-.139-2.076-.07-.174.021-.201-.13-.045-.241 1.018-.713 2.685-.508 2.879-.269.194.241-.052 1.906-.997 2.701-.147.123-.287.058-.222-.104.215-.535.689-1.725.461-2.017z' },
                  { name: 'Tidal', color: '#000000', url: 'https://tidal.com/artist/10418501', icon: 'M12.012 3.992L8.008 7.996 4.004 3.992 0 7.996 4.004 12l4.004-4.004L12.012 12l4.004-4.004L12.012 3.992zM12.012 12l-4.004 4.004L12.012 20.008l4.004-4.004L12.012 12zM20.02 7.996L16.016 12l4.004 4.004L24.024 12l-4.004-4.004z' },
                  { name: 'Deezer', color: '#FEAA2D', url: 'https://www.deezer.com/us/artist/52099312', icon: 'M18.81 4.16v3.03H24V4.16h-5.19zM6.27 8.38v3.027h5.189V8.38h-5.19zm12.54 0v3.027H24V8.38h-5.19zM6.27 12.594v3.027h5.189v-3.027h-5.19zm6.27 0v3.027h5.19v-3.027h-5.19zm6.27 0v3.027H24v-3.027h-5.19zM0 16.81v3.029h5.19v-3.03H0zm6.27 0v3.029h5.189v-3.03h-5.19zm6.27 0v3.029h5.19v-3.03h-5.19zm6.27 0v3.029H24v-3.03h-5.19z' },
                  { name: 'SoundCloud', color: '#FF5500', url: 'https://soundcloud.com/the-lost-strings', icon: 'M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.1-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c.014.057.045.094.09.094.042 0 .075-.038.09-.094l.21-1.308-.21-1.332c-.016-.057-.048-.094-.09-.094m1.83-1.229c-.061 0-.12.045-.12.104l-.21 2.563.225 2.458c0 .06.045.104.106.104.061 0 .12-.044.12-.104l.24-2.458-.24-2.563c0-.06-.059-.104-.12-.104m.945-.089c-.075 0-.135.06-.15.135l-.193 2.64.21 2.544c.016.077.075.138.149.138.075 0 .135-.061.15-.138l.24-2.544-.24-2.64c-.015-.075-.075-.135-.15-.135l-.016.001zm1.155.36c-.005-.09-.075-.149-.159-.149-.09 0-.158.06-.164.149l-.217 2.43.2 2.563c0 .09.075.157.159.157.074 0 .148-.068.148-.158l.227-2.563-.227-2.43.033.001zm.809-1.709c-.101 0-.18.09-.18.181l-.21 3.957.187 2.563c0 .09.08.164.18.164.094 0 .174-.09.18-.18l.209-2.563-.209-3.972c-.008-.104-.088-.18-.18-.18m.959-.914c-.105 0-.195.09-.203.194l-.18 4.872.165 2.548c0 .12.09.209.195.209.104 0 .194-.089.21-.209l.193-2.548-.192-4.856c-.016-.12-.105-.21-.21-.21m.989-.449c-.121 0-.211.089-.225.209l-.165 5.275.165 2.52c.014.119.104.225.225.225.119 0 .225-.105.225-.225l.195-2.52-.196-5.275c0-.12-.105-.225-.225-.209m1.245.045c0-.135-.105-.24-.24-.24-.119 0-.24.105-.24.24l-.149 5.441.149 2.503c.016.135.121.24.256.24s.24-.105.24-.24l.164-2.503-.164-5.456-.016.015zm.749-.134c-.135 0-.255.119-.255.254l-.15 5.322.15 2.473c0 .15.12.255.255.255s.255-.12.255-.27l.15-2.458-.15-5.32c0-.135-.12-.256-.255-.256m1.005.166c-.164 0-.284.135-.284.285l-.103 5.143.135 2.474c0 .149.119.277.284.277.149 0 .271-.12.284-.285l.121-2.443-.135-5.112c-.012-.164-.135-.285-.285-.285m1.184-.945c-.016-.18-.165-.314-.33-.314-.181 0-.33.135-.33.314l-.135 6.457.135 2.458c0 .179.149.33.315.33.165 0 .314-.151.33-.315l.149-2.473-.149-6.442.015-.015zm.96-.028c-.194 0-.36.15-.36.359l-.12 6.457.12 2.442c.015.209.166.359.359.359.195 0 .36-.165.36-.359l.135-2.457-.135-6.457c0-.209-.165-.359-.359-.359m1.05-.449c-.21 0-.375.18-.391.39l-.12 6.898.12 2.41c.016.211.181.375.391.375.21 0 .391-.164.391-.375l.135-2.412-.135-6.9c-.015-.209-.18-.39-.39-.39m1.095.156c-.225 0-.405.165-.42.39l-.105 6.355.105 2.412c.015.225.195.39.42.39.21 0 .405-.165.42-.405l.105-2.397-.105-6.355c-.015-.239-.195-.404-.42-.404m1.08.384c-.24 0-.42.195-.435.42l-.105 5.95.105 2.399c.016.24.196.42.435.42.24 0 .42-.179.435-.42l.106-2.4-.106-5.95c-.015-.225-.21-.419-.435-.419m1.095.27c-.254 0-.435.194-.45.45l-.09 5.644.09 2.4c.015.255.195.435.45.435.254 0 .45-.18.45-.45l.105-2.385-.105-5.644c0-.256-.195-.45-.45-.45m4.065.705c-.179 0-.33.06-.465.149-.15-.645-.615-1.095-1.155-1.095-.135 0-.285.03-.42.09-.165.075-.24.135-.24.255v5.489c0 .12.105.225.24.255.029.016.524.016.555 0 2.115-.031 3.84-1.379 3.84-3.075 0-1.14-1.139-2.07-2.355-2.07' },
                  { name: 'Pandora', color: '#005483', url: 'https://www.pandora.com/artist/the-lost-strings', icon: 'M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 19.5c-4.135 0-7.5-3.365-7.5-7.5S7.865 4.5 12 4.5s7.5 3.365 7.5 7.5-3.365 7.5-7.5 7.5zm0-12c-2.485 0-4.5 2.015-4.5 4.5s2.015 4.5 4.5 4.5 4.5-2.015 4.5-4.5-2.015-4.5-4.5-4.5z' },
                ].map((platform) => (
                  <button
                    key={platform.name}
                    onClick={() => window.open(platform.url, '_blank')}
                    className="group flex flex-col items-center gap-2 p-3 rounded-xl border bg-card hover:bg-accent/50 transition-all duration-200 hover:scale-105 hover:shadow-md"
                    style={{ '--platform-color': platform.color } as React.CSSProperties}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200"
                      style={{ backgroundColor: `${platform.color}15` }}
                    >
                      <svg 
                        className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" 
                        viewBox="0 0 24 24" 
                        fill={platform.color}
                      >
                        <path d={platform.icon} />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                      {platform.name}
                    </span>
                  </button>
                ))}
              </div>

              {/* Embedded Players */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Spotify Embed */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#1DB954]/10 flex items-center justify-center">
                      <svg className="h-4 w-4 text-[#1DB954]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                    </div>
                    <h3 className="font-semibold">Listen on Spotify</h3>
                  </div>
                  <iframe
                    src="https://open.spotify.com/embed/artist/2EldLZ5d0hetsPUwgkIzrF?utm_source=generator&theme=0"
                    width="100%"
                    height="352"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    className="rounded-xl"
                  />
                </div>

                {/* Instagram Embed */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#E4405F]/10 flex items-center justify-center">
                      <svg className="h-4 w-4 text-[#E4405F]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                      </svg>
                    </div>
                    <h3 className="font-semibold">Follow on Instagram</h3>
                  </div>
                  <div 
                    className="rounded-xl border bg-card overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => window.open('https://www.instagram.com/theloststrings/', '_blank')}
                  >
                    <div className="p-6 flex flex-col items-center justify-center h-[352px] bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45]">
                      <svg className="h-16 w-16 text-white mb-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                      </svg>
                      <h4 className="text-white text-xl font-bold mb-1">@theloststrings</h4>
                      <p className="text-white/80 text-sm mb-4">Follow for behind-the-scenes & updates</p>
                      <div className="bg-white text-[#E4405F] px-6 py-2 rounded-full font-semibold text-sm hover:bg-white/90 transition-colors">
                        Follow Us
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab for All Users */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
                <CardDescription>
                  Manage your account preferences and security
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Password</h3>
                  <p className="text-sm text-muted-foreground">
                    Update your password to keep your account secure.
                  </p>
                  <SetPasswordDialog />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="admin" className="animate-fade-in">
              {/* Admin Hero Banner */}
              <div className="relative mb-6 overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-accent/5 p-6">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/10 rounded-full blur-3xl" />
                
                <div className="relative z-10 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
                    <LayoutDashboard className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-foreground via-primary to-accent bg-clip-text text-transparent">
                      Command Center
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Manage your business, track performance, and control everything
                    </p>
                  </div>
                </div>
              </div>

              <Tabs defaultValue="executive" className="space-y-4">
                <TabsList className="bg-muted/50 border border-border/50 p-1 h-auto flex-wrap">
                  <TabsTrigger 
                    value="executive"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-primary/10 data-[state=active]:shadow-sm"
                  >
                    <PieChart className="h-4 w-4 mr-2" />
                    Executive
                  </TabsTrigger>
                  <TabsTrigger 
                    value="inquiries-admin"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-primary/10 data-[state=active]:shadow-sm"
                  >
                    <Inbox className="h-4 w-4 mr-2" />
                    CRM
                  </TabsTrigger>
                  <TabsTrigger 
                    value="management"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-primary/10 data-[state=active]:shadow-sm"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Content
                  </TabsTrigger>
                  <TabsTrigger 
                    value="files"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-primary/10 data-[state=active]:shadow-sm"
                  >
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Files
                  </TabsTrigger>
                  <TabsTrigger 
                    value="users"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-primary/10 data-[state=active]:shadow-sm"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Users
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="executive" className="animate-fade-in">
                  <ExecutiveDashboard />
                </TabsContent>

                <TabsContent value="inquiries-admin" className="animate-fade-in">
                  <InquiriesTable />
                </TabsContent>

                <TabsContent value="management" className="animate-fade-in">
                  <div className="space-y-6">
                    <SiteSettingsManager />
                    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                      <CardHeader>
                        <CardTitle>Team Management</CardTitle>
                        <CardDescription>
                          Manage team members displayed on the homepage
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <TeamManager />
                      </CardContent>
                    </Card>
                    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                      <CardHeader>
                        <CardTitle>Highlights Manager</CardTitle>
                        <CardDescription>
                          Manage site highlights and media content
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <HighlightsManager />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="files" className="animate-fade-in">
                  <FilesManager />
                </TabsContent>

                <TabsContent value="users" className="animate-fade-in">
                  <AdminUserManager />
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
